// MenuScene — title, a one-tap seed PLAY, a real TOPIC ENTRY (factual topic), Surprise, Daily,
// recents, and the existing seed-pack quick-picks + AI toggle. Submitting a topic shows an animated
// "building verified board…" state, calls the M1 board API (boardClient.fetchBoard), then starts the
// GameScene with the returned BoardContent — falling back to a matching seed board on any failure,
// or showing a friendly inline message if nothing matches.
//
// The topic field is an HTML <input> overlaid on the canvas (main.ts does not enable Phaser DOM
// container, and an overlay is the most reliable mobile text-entry surface). It is created on
// create() and destroyed on shutdown so it never leaks across scene swaps or the capture harness.
import Phaser from 'phaser';
import { SEED_TOPICS } from '../../data/seedPack';
import type { BoardContent } from '../../core';
import { PALETTE, hex, FONT } from '../keys';
import { REG, readBoot } from '../registryKeys';
import { fetchBoard, getDaily } from '../../net/boardClient';
import type { FetchBoardResult, BoardErrorReason } from '../../net/types';
import { audio } from '../audio';
import { addBackdrop } from '../backdrop';
import { isLandscape } from '../stage';
import { openSettings } from '../settingsPanel';
import { motionAllowed, scaledFont } from '../settings';
import { openStats } from '../statsPanel';
import { openDisclosure } from '../disclosurePanel';
import { openLocker } from '../lockerPanel';
import { openCustomLab } from '../customLabPanel';
import { openTodayGauntlet } from '../todayGauntletPanel';
import type { LadderRace } from '../ghostLadder';
import { track, getExperimentVariant, type BoardSource } from '../../analytics/client';
import {
  liveDailyStreak,
  dailyDoneToday,
  getStats,
  getProgress,
  getDailyMissions,
  type MissionView,
} from '../stats';
import { openOnboarding, onboardingSeen } from '../onboarding';
import { noviceAiEnabled, noviceBoardFor, NOVICE_TOPIC_CATEGORY } from '../noviceRun';
import { homeModel, type HomeModel } from '../menuState';
import { selectedCosmetic, takeUnlockCelebrations } from '../cosmetics';
import { iconButton, chip, ctaButton, secondaryButton, labelText, progressBar, cardPanel } from '../ui/kit';
import { icon, drawIcon, type IconName } from '../icons';
import { resolveLaunchTopicLabel } from '../topicLabel';
import { attachMascot } from '../mascot';
import { styleHeroText } from '../juice';
import { openOfflinePanel, showOfflineBanner } from '../offline';
import { parseSeries, type SeriesState } from '../series';
import * as LoadingOverlay from '../menu/loadingOverlay';
import * as TopicInput from '../menu/topicInput';

interface TopicRow {
  bg: Phaser.GameObjects.Rectangle;
  label: Phaser.GameObjects.Text;
  normalized: string; // seed topicNormalized slug
  raw: string; // human label, used as the topic to play
}

type LaunchKind = 'quick_pick' | 'selected_seed' | 'recent' | 'surprise' | 'custom' | 'daily' | 'deep_link';

const RECENTS_KEY = 'trivia.recents.v1';
const MAX_RECENTS = 4;
// V2-R1: the returning-user mode choice (rival vs practice). Absent = never chosen → organic default is
// RIVAL (VS BYT3). A returning user who explicitly toggles PRACTICE is remembered and respected.
const MODE_PREF_KEY = 'trivia.rivalMode.v1';

// #8 — the celebrity-slug foregrounding filter (CELEBRITY_TOPIC_SLUGS / FEATURABLE_TOPICS) is deleted:
// its only entry, `music::taylor_swift_albums`, no longer exists in the seed pack (S8 IP sweep), so the
// filter was a no-op that shadowed SEED_TOPICS everywhere. The menu now reads SEED_TOPICS directly; git
// history preserves the mechanism if a living-person foregrounding filter is ever needed again.
// The 4 featured quick-pick slots (neutral spread: mythology / astronomy / nature / geography —
// original-safe per the S8 IP sweep; the old Pixar/NBA/World-Cup chips carried third-party marks).
// Any slug miss falls back to the next seed topic so the grid always fills.
const FEATURED_QUICK_PICK_SLUGS = [
  'mythology::greek_gods',
  'astronomy::solar_system_planets',
  'nature::dinosaurs',
  'geography::us_state_capitals',
];
// #5 — whole-phrase labels for the featured quick-pick chips. shortLabel()'s generic 2-word/11-char trim
// bisects some of these ("Planets in our solar system" → "Planets in"; "U.S. state capitals" → "U.S.
// state"); an explicit override keeps every chip a COMPLETE phrase at 100% AND 150% text scale (the chip
// still auto-shrinks to fit its cell — it never truncates mid-phrase).
const QUICK_PICK_SHORT_LABELS: Record<string, string> = {
  'mythology::greek_gods': 'Greek Gods',
  'astronomy::solar_system_planets': 'Planets',
  'nature::dinosaurs': 'Dinosaurs',
  'geography::us_state_capitals': 'State Capitals',
};
function featuredQuickPicks(): typeof SEED_TOPICS {
  const bySlug = new Map(SEED_TOPICS.map((t) => [t.normalized, t]));
  const picks = FEATURED_QUICK_PICK_SLUGS.map((s) => bySlug.get(s)).filter(
    (t): t is (typeof SEED_TOPICS)[number] => !!t,
  );
  for (const t of SEED_TOPICS) {
    if (picks.length >= 4) break;
    if (!picks.includes(t)) picks.push(t);
  }
  return picks;
}

export class MenuScene extends Phaser.Scene {
  // NOTE (S4 SPLIT): fields are PUBLIC so the extracted menu/* controllers (bound as class fields
  // below via `export function f(this: MenuScene)` — the S2 idiom) can read/write them. Behaviour is
  // unchanged; only the access modifier was widened.
  aiEnabled = true;
  rows: TopicRow[] = [];
  selected: string | null = null; // selected seed slug | null

  // M1 topic-entry overlay + state
  inputEl?: HTMLInputElement;
  messageText?: Phaser.GameObjects.Text;
  loading = false;
  loadingLayer?: Phaser.GameObjects.Container;
  loadingTween?: Phaser.Tweens.Tween;
  loadingDotsEvent?: Phaser.Time.TimerEvent;
  /** Live verify-checklist rows in the board-cook overlay (icon + label + spin per step). */
  cookSteps: Array<{
    icon: Phaser.GameObjects.Graphics;
    label: Phaser.GameObjects.Text;
    state: 'pending' | 'active' | 'done';
    spin?: Phaser.Tweens.Tween;
  }> = [];
  /** Furthest REAL board-fetch phase the checklist has shown (dedupes repeated poll callbacks). */
  cookPhase: 'request' | 'accepted' | 'verified' = 'request';
  /** Earliest scene-time the next checklist repaint may land (enforces the >=150ms visible cascade). */
  cookNextAt = 0;
  inflight?: AbortController;
  /** keep the canvas-position-sync ticking while the overlay is alive */
  syncEvent?: Phaser.Time.TimerEvent;
  /** M3: true while a Daily launch is in flight, so startWithContent flags the run as daily. */
  launchingDaily = false;
  /** GHOST DUELS Slice A: true when the in-flight Daily is a "Race yesterday's you" self-race launch. */
  launchingRaceGhost = false;
  /** GHOST DUELS Slice C: the ladder challenger the in-flight Daily is duelling (record + rung), or null. */
  launchingLadder: LadderRace | null = null;
  /** M3: human label for the run being launched (for the share/leaderboard copy on the result). */
  launchingLabel = '';
  /** P0 analytics: how the current board launch began. */
  launchingKind: LaunchKind = 'quick_pick';
  /** P1.2: true when the current launch came from a deep link (incl. the daily branch) so the
   *  challenge target/ghost/series are carried even though playDaily sets launchingKind='daily'. */
  launchedFromDeepLink = false;
  /** P0 analytics: generated vs seed fallback source for the current board. */
  launchingBoardSource: BoardSource = 'unknown';

  /** M3 deep-link: a topic to auto-launch (from a shared "beat my score" link). */
  deepLinkTopic = '';
  deepLinkDaily = false;

  /** G11: true while the first-run/how-to tutorial overlay is up, to suppress menu launches under it. */
  onboarding = false;

  /** Offline graceful-fallback banner, when the device reports no network (dismissible). */
  offlineBanner?: Phaser.GameObjects.Container;
  /** One-shot guard so a flaky network event cannot stack repeated full offline panels. */
  offlinePanelShown = false;

  constructor() {
    super('Menu');
  }

  init(data: { deepLinkTopic?: string; deepLinkDaily?: boolean } = {}): void {
    this.deepLinkTopic = data.deepLinkTopic ?? '';
    this.deepLinkDaily = data.deepLinkDaily ?? false;
  }

  create(): void {
    const boot = readBoot(this);
    // V2-R1 RIVAL PRESENCE — the returning-user ORGANIC default is VS BYT3 (rival ON); practice is now the
    // explicit opt-out. Precedence: an explicit `?ai=` URL override wins (harness + deep links); else a
    // returning user's STORED explicit choice is respected (someone who picked practice keeps practice);
    // else the never-chosen default is RIVAL. This flip does NOT touch the first-run novice choreography —
    // a brand-new player still routes through startNoviceRun()/noviceAiEnabled(0), which ignores this flag
    // (the rival formally joins in training board 3, exactly as designed). Ladder duels force ai on
    // downstream (startWithContent), and rivalWarmup semantics are unchanged.
    const storedMode = this.readModePref();
    this.aiEnabled = new URLSearchParams(window.location.search).has('ai') ? boot.ai : storedMode ?? true;
    this.selected = null;
    this.rows = [];
    this.loading = false;
    this.launchingDaily = false;
    this.launchingLabel = '';
    this.launchingKind = 'quick_pick';
    this.launchingBoardSource = 'unknown';
    this.offlinePanelShown = false;

    const cam = this.cameras.main;
    cam.setBackgroundColor(PALETTE.bg);
    addBackdrop(this, 'menu');
    const W = cam.width;
    const H = cam.height;
    // LANDSCAPE STAGE (S4): compose the home for WIDTH — a hero/brand panel on the LEFT, the action
    // column (CTA / topic / surprise·daily / quick-picks / mode) on the RIGHT — instead of a centred
    // phone sliver. `cx` becomes the action-column centre so every action element re-homes there;
    // `heroCx` anchors the left panel; `sizeW` is a fixed width basis for font clamps so desktop type
    // lands at comfortable sizes (not ballooned off the 1280 design width). Portrait keeps cx = W/2.
    const land = isLandscape();
    const cx = land ? Math.round(W * 0.625) : W / 2;
    const heroCx = land ? Math.round(W * 0.3) : W / 2;
    const sizeW = land ? 560 : W;

    // M2 AUDIO: bind the bus to this game, unlock the WebAudio context on the first user gesture
    // (browser autoplay policy), and start the calm menu bed. Headless captures have no gesture so
    // the context stays suspended and these are silent no-ops.
    audio().attach(this);
    audio().playMenuMusic();
    this.input.once('pointerdown', () => audio().unlock());
    this.input.keyboard?.once('keydown', () => audio().unlock());

    // ── Progressive-disclosure home model (new vs. returning) ──
    // Returning treatment cohort => the hero plays Today's Gauntlet directly (heroDaily); the
    // 'classic_home' holdout keeps the seed-PLAY hero. New players always get START TRAINING.
    const firstRunTraining = !boot.debug && !onboardingSeen();
    const home = homeModel({
      introDone: !firstRunTraining,
      candidates: SEED_TOPICS.map((t) => ({ normalized: t.normalized, label: t.raw })),
      variant: getExperimentVariant(),
    });
    const heroDaily = home.primary.action === 'daily';
    const isNew = home.mode === 'new';

    // #39 graduation handshake: a player who JUST finished training lands here with a one-shot registry
    // flag (armed by ResultScene on the training-final result). Read + CLEAR it now so the spotlight runs
    // exactly once, then — after the returning hub's affordances are built below — sweep BUILD / DAILY /
    // RIVAL so a fresh graduate is pointed at their next choices. A graduate always has onboardingSeen()=
    // true, so this only ever fires on the returning hub. Was a dead flag until now (only the setter existed).
    const justGraduated = this.registry.get('trivia.justGraduated.v1') === true;
    if (justGraduated) this.registry.remove('trivia.justGraduated.v1');
    // Collected during the returning-hub build so the sweep can pulse each real affordance in place.
    const gradTargets: { x: number; y: number; w: number; h: number; label: string }[] = [];

    // Subtle content frame (Tactical OLED card). Landscape: a card that HUGS the right action column
    // (the left hero panel floats on the treated stage), so the composition reads as a designed
    // two-pane desktop layout rather than one narrow centred sliver.
    this.add
      .rectangle(cx, H / 2, land ? 512 : Math.min(W * 0.94, 560), land ? H * 0.86 : H * 0.95, PALETTE.surface, 0.22)
      .setStrokeStyle(1.5, PALETTE.line, 0.5)
      .setDepth(0);
    // Landscape: a faint hero panel behind the left brand/mascot so the two panes read as a pair.
    if (land) {
      this.add
        .rectangle(heroCx, H / 2, 420, H * 0.86, PALETTE.surface, 0.12)
        .setStrokeStyle(1.5, PALETTE.line, 0.35)
        .setDepth(0);
    }

    // Top utility cluster (mode-aware: returning = palette/chart/gear, new = question/gear).
    this.buildTopRightButtons(W, home.mode);

    const colW = Math.min(sizeW * 0.86, 470);
    const ctaH = 60;
    const compactPortraitHub = !land && !isNew;
    const ctaY = isNew ? (land ? H * 0.5 : H * 0.535) : compactPortraitHub ? H * 0.275 : H * 0.295;

    // Mobile returning players still need the brand in the first viewport; wide returning hub gets it
    // inside the left hero rail below.
    if (isNew || !land) {
      const fs = isNew ? Math.max(24, Math.min(44, W * 0.064)) : Math.max(24, Math.min(34, W * 0.062));
      const titleY = !land && !isNew ? H * 0.108 : H * 0.092;
      const trk = Math.round(fs * 0.02);
      const t1 = this.add
        .text(0, titleY, 'TRIVIA', { fontFamily: FONT.display, fontSize: `${fs}px`, color: '#ffffff', fontStyle: 'bold' })
        .setOrigin(0, 0.5);
      const t2 = this.add
        .text(0, titleY, 'MUNCHERS', { fontFamily: FONT.display, fontSize: `${fs}px`, color: '#ffffff', fontStyle: 'bold' })
        .setOrigin(0, 0.5);
      // M8 — heroWordmark language (two-tone gradient + dark outline) with BRAND colours (chrome + cyan);
      // widths are measured AFTER styling (tracking/stroke change metrics) so the adjacency stays exact.
      styleHeroText(t1, { top: '#f5f8ff', bottom: '#b7c1d6', tracking: trk, stroke: '#0a0b12' });
      styleHeroText(t2, { top: '#9bf1ff', bottom: '#1ec3e6', tracking: trk, stroke: '#062028' });
      const gap = Math.round(fs * 0.26);
      const wmW = t1.width + gap + t2.width;
      const brandCx = land ? W / 2 : cx;
      t1.setX(brandCx - wmW / 2);
      t2.setX(brandCx - wmW / 2 + t1.width + gap);
    }

    // Landscape returning hub: the left panel earns a brand wordmark above the diorama (the splash's
    // title beat isn't on-screen here, and a wide panel with only a mascot reads empty).
    if (land && !isNew) {
      const fs = 34;
      const trk = Math.round(fs * 0.02);
      const t1 = this.add
        .text(0, H * 0.2, 'TRIVIA', { fontFamily: FONT.display, fontSize: `${fs}px`, color: '#ffffff', fontStyle: 'bold' })
        .setOrigin(0, 0.5).setDepth(4);
      const t2 = this.add
        .text(0, H * 0.2, 'MUNCHERS', { fontFamily: FONT.display, fontSize: `${fs}px`, color: '#ffffff', fontStyle: 'bold' })
        .setOrigin(0, 0.5).setDepth(4);
      // M8 — heroWordmark language (gradient + outline) with BRAND colours; measure widths post-style.
      styleHeroText(t1, { top: '#f5f8ff', bottom: '#b7c1d6', tracking: trk, stroke: '#0a0b12' });
      styleHeroText(t2, { top: '#9bf1ff', bottom: '#1ec3e6', tracking: trk, stroke: '#062028' });
      const gap = Math.round(fs * 0.26);
      const wmW = t1.width + gap + t2.width;
      t1.setX(heroCx - wmW / 2);
      t2.setX(heroCx - wmW / 2 + t1.width + gap);
      const tag = this.add
        .text(heroCx, H * 0.26, 'AN ARCADE THAT HAPPENS TO BE TRIVIA', { fontFamily: FONT.display, fontSize: '12px', color: hex(PALETTE.dim), fontStyle: '600' })
        .setOrigin(0.5)
        .setDepth(4);
      tag.setLetterSpacing?.(2);
      const tagMax = 310;
      if (tag.width > tagMax) tag.setScale(tagMax / tag.width);
    }

    // Hero: BOTH new + returning now get the composed character-in-arena moment. #13/#17 director ruling
    // — the new-player hero is the TEMPLATE, borrowed onto the returning hub (which used to float a lone
    // 76px muncher in a faint diorama). New player: +30% mobile hero + a one-shot CHOMP+score pop on first
    // open. Returning: the same arena (glow plinth + orbiting ?/✓/# chips + strong grid) + the BYT3 rival.
    if (isNew) {
      const newHeroSize = land ? Math.max(136, Math.min(158, H * 0.2)) : Math.max(96, Math.min(132, H * 0.16));
      this.buildArenaHero(land ? heroCx : cx, land ? H * 0.5 : H * 0.315, newHeroSize, sizeW, { chomp: true });
    }
    else this.buildArenaHero(land ? heroCx : cx, land ? H * 0.5 : H * 0.205, land ? 112 : 84, sizeW, { rival: true });
    // (Seasonal-pack banner intentionally omitted here — the redline menu is clean above the CTA.)

    // ── Primary CTA: full-width green, play icon (scout: ref shows a play glyph, not bolt/sparkle),
    //    wrapped in a thin gold ring on the returning hub ──
    const heroLabel = isNew
      ? home.primary.label
      : heroDaily
        ? home.dailyDone
          ? "REPLAY TODAY'S GAUNTLET ✓"
          : "PLAY TODAY'S GAUNTLET"
        : this.aiEnabled
          ? 'PLAY WARMUP'
          : 'PLAY PRACTICE';
    let primaryCta = this.buildPrimaryCta(
      cx,
      ctaY,
      colW,
      ctaH,
      heroLabel,
      'play',
      () => {
        if (!isNew && heroDaily) this.playDaily();
        else this.playSelectedSeed();
      },
      !isNew, // gold ring only on the returning-hub daily CTA, not new-player START TRAINING
    );
    // The seed-PLAY (classic_home) hero relabels WARMUP/PRACTICE with the rival toggle — rebuild it.
    const rebuildPrimary = (): void => {
      primaryCta.destroy();
      primaryCta = this.buildPrimaryCta(
        cx,
        ctaY,
        colW,
        ctaH,
        this.aiEnabled ? 'PLAY WARMUP' : 'PLAY PRACTICE',
        'play',
        () => this.playSelectedSeed(),
      );
    };

    // P1.1 PROGRESSIVE DISCLOSURE: a brand-new player sees ONLY the hero + a short pitch (built above
    // is the CTA; the pitch/locked/up-next sits below). Returning players get the full discovery hub:
    // topic entry, surprise/daily, quick-picks, and the pinned MODE footer.
    if (isNew) {
      this.buildNewPlayerHome(cx, sizeW, colW, H, ctaY, ctaH, home);
    } else {
      // ── TOPIC ENTRY: input tile (2px scan border) + inline BUILD (scan fill, ink) ──
      const topicY = land ? H * 0.405 : H * 0.355;
      const inputH = 46;
      const buildW = Math.min(98, colW * 0.24);
      const gapTI = 10;
      const inputW = colW - buildW - gapTI;
      const inputCx = cx - colW / 2 + inputW / 2;
      const buildCx = cx + colW / 2 - buildW / 2;
      const inputFrame = this.add
        .rectangle(inputCx, topicY, inputW, inputH, PALETTE.surface)
        .setStrokeStyle(2, PALETTE.scan, 0.95);
      this.createTopicInput(inputFrame);
      const buildG = this.add.graphics();
      buildG.fillStyle(PALETTE.scan, 1);
      buildG.fillRoundedRect(buildCx - buildW / 2, topicY - inputH / 2, buildW, inputH, 10);
      this.add
        .text(buildCx, topicY, 'BUILD', {
          fontFamily: FONT.display,
          fontSize: `${scaledFont(Math.max(13, sizeW * 0.026), 12)}px`,
          color: hex(PALETTE.ink),
          fontStyle: 'bold',
        })
        .setOrigin(0.5);
      const buildHit = this.add.rectangle(buildCx, topicY, buildW, inputH, 0xffffff, 0.001).setInteractive({ useHandCursor: true });
      buildHit.on('pointerup', () => {
        audio().playSfx('uiConfirm');
        this.submitTyped();
      });
      // #39: the topic BUILD affordance — "make your own board" — is the first spotlight beat for a graduate.
      gradTargets.push({ x: buildCx, y: topicY, w: buildW, h: inputH, label: 'BUILD' });

      // (Custom Lab link removed for a clean redline build — custom topics are crafted via the
      //  topic input + BUILD; the full Custom Lab panel remains available to deep links / debug.)

      // ── SECONDARY ROW: SURPRISE (secondary) + DAILY · streak pill (armed) ──
      const secY = land ? H * 0.485 : topicY + 58;
      const halfW = (colW - 10) / 2;
      const sX = cx - colW / 2 + halfW / 2;
      const dX = cx + colW / 2 - halfW / 2;
      secondaryButton(this, sX, secY, 'SURPRISE', () => this.surpriseMe(), {
        width: halfW,
        height: 38,
        icon: 'shuffle',
        textColor: PALETTE.text,
        fontPx: 14,
      });

      const stats = getStats();
      const streak = liveDailyStreak(stats);
      const doneToday = dailyDoneToday(stats);
      if (land) this.buildDesktopHomeRails(W, H, stats, doneToday);
      const dCol = doneToday ? PALETTE.correct : PALETTE.armed;
      const dLabel = doneToday ? 'DAILY DONE' : streak > 0 ? `DAILY · ${streak}D` : 'DAILY';
      const dpg = this.add.graphics();
      dpg.fillStyle(PALETTE.surface, 1);
      dpg.fillRoundedRect(dX - halfW / 2, secY - 19, halfW, 38, 10);
      dpg.lineStyle(1.25, dCol, 0.9);
      dpg.strokeRoundedRect(dX - halfW / 2, secY - 19, halfW, 38, 10);
      const dTxt = this.add
        .text(0, secY, dLabel, { fontFamily: FONT.display, fontSize: `${scaledFont(14, 12)}px`, color: hex(dCol), fontStyle: 'bold' })
        .setOrigin(0, 0.5);
      const dIcS = 16;
      const dTotal = dIcS + 7 + dTxt.width;
      const dStart = dX - dTotal / 2;
      icon(this, doneToday ? 'check' : 'flame', dStart + dIcS / 2, secY, dIcS, dCol);
      dTxt.setX(dStart + dIcS + 7);
      const dHit = this.add.rectangle(dX, secY, halfW, Math.max(38, 44), 0xffffff, 0.001).setInteractive({ useHandCursor: true });
      dHit.on('pointerup', () => {
        audio().playSfx('uiTap');
        this.hideTopicInput();
        openTodayGauntlet(this, { onPlay: (o) => this.playDaily(o) });
      });
      // #39: the DAILY affordance — "Today's Gauntlet" — is the second spotlight beat for a graduate.
      gradTargets.push({ x: dX, y: secY, w: halfW, h: 38, label: 'DAILY' });

      // #13 — the running bottom of the action stack (set by the quick-picks grid), so the MODE footer
      // un-pins from the very bottom of the tall stage and seats directly under the last picks row.
      let stackBottomY = H * 0.62;
      // ── QUICK PICKS (#54): a 2×2 grid of TOY chips — 16px category glyph + a category-color accent
      //    stripe + press-scale 0.96 — so they read as things to grab, not a flat list of identical rects.
      const picksLabelY = land ? H * 0.55 : secY + 52;
      labelText(this, cx - colW / 2, picksLabelY, 'Quick picks', { size: 12 });
      const picksTop = picksLabelY + Math.max(26, H * 0.034);
      const picks = featuredQuickPicks();
      const pickGap = 10;
      const pickW = (colW - pickGap) / 2;
      const pickH = Math.max(38, Math.min(48, H * 0.046));
      const pickRowGap = 10;
      const pickFs = scaledFont(Math.max(12, Math.min(15, pickH * 0.36)), 12);
      picks.forEach((t, i) => {
        const col = i % 2;
        const row = Math.floor(i / 2);
        const x = cx - colW / 2 + pickW / 2 + col * (pickW + pickGap);
        const y = picksTop + row * (pickH + pickRowGap);
        const cat = this.categoryGlyph(t.normalized);
        const cont = this.add.container(x, y);
        const g = this.add.graphics();
        const paint = (hover: boolean): void => {
          g.clear();
          g.fillStyle(hover ? PALETTE.tileHot : PALETTE.surface, 1);
          g.fillRoundedRect(-pickW / 2, -pickH / 2, pickW, pickH, 10);
          g.lineStyle(1.25, hover ? cat.color : PALETTE.lineStrong, 1);
          g.strokeRoundedRect(-pickW / 2, -pickH / 2, pickW, pickH, 10);
          // Category accent stripe hugging the left edge (rounds into the left corners).
          g.fillStyle(cat.color, hover ? 1 : 0.85);
          g.fillRoundedRect(-pickW / 2, -pickH / 2, 5, pickH, { tl: 10, bl: 10, tr: 0, br: 0 });
        };
        paint(false);
        const glyphX = -pickW / 2 + 22;
        const gl = icon(this, cat.icon, glyphX, 0, 16, cat.color);
        const label = this.add
          .text(glyphX + 16, 0, QUICK_PICK_SHORT_LABELS[t.normalized] ?? this.shortLabel(t.raw), { fontFamily: FONT.display, fontSize: `${pickFs}px`, color: hex(PALETTE.text), fontStyle: '600' })
          .setOrigin(0, 0.5);
        const labelRoom = pickW / 2 - 12 - (glyphX + 16);
        if (label.width > labelRoom) label.setScale(labelRoom / label.width);
        const hit = this.add.rectangle(0, 0, pickW, pickH, 0xffffff, 0.001).setInteractive({ useHandCursor: true });
        cont.add([g, gl, label, hit]);
        hit.on('pointerover', () => paint(true));
        hit.on('pointerout', () => { paint(false); cont.setScale(1); });
        hit.on('pointerdown', () => cont.setScale(0.96)); // #54 press-scale (snappy, reduceMotion-safe)
        hit.on('pointerup', () => {
          cont.setScale(1);
          audio().playSfx('uiTap');
          this.playTopic(t.raw, 'quick_pick');
        });
        // Keep the seed-selection wiring intact (classic_home PLAY hero + Enter key launch this).
        this.rows.push({ bg: hit, label, normalized: t.normalized, raw: t.raw });
      });
      if (this.rows.length > 0) this.selected = this.rows[0].normalized;
      // #13 — the live bottom of the quick-picks grid; the MODE footer seats just below it (un-pinned).
      const nRows = Math.ceil(picks.length / 2);
      stackBottomY = picksTop + (nRows - 1) * (pickH + pickRowGap) + pickH / 2;

      // ── Inline message (fallback / friendly errors) ──
      this.messageText = this.add
        .text(cx, H * 0.7, '', {
          fontFamily: FONT.family,
          fontSize: `${scaledFont(Math.max(11, sizeW * 0.021), 11)}px`,
          color: hex(PALETTE.armed),
          align: 'center',
          wordWrap: { width: colW },
        })
        .setOrigin(0.5, 0)
        .setDepth(5);

      // ── MODE footer (rival/practice) ──
      // #13 UN-PIN: portrait used to pin this to the very bottom (H−26), stranding a dead THIRD between the
      // quick-picks grid and the pill. Seat it just BELOW the last picks row instead (in the action stack),
      // closing that void. Landscape keeps its in-column seat near the card's lower edge.
      const tallPortraitHome = !land && H >= 1400;
      const footerY = land
        ? Math.round(H * 0.8)
        : tallPortraitHome
          ? Math.min(H - 220, stackBottomY + 34)
          : Math.min(H - 44, stackBottomY + 32);
      // Move the inline error line clear of the re-seated pill (below it, out of the interactive stack).
      this.messageText?.setY(land ? H * 0.72 : Math.min(H - 20, footerY + 30));
      const footerW = colW;
      const footerH = 34;
      const footerG = this.add.graphics();
      const modeMain = this.add
        .text(0, footerY, '', { fontFamily: FONT.display, fontSize: `${scaledFont(14, 12)}px`, color: hex(PALETTE.text), fontStyle: 'bold' })
        .setOrigin(0, 0.5);
      const modeSub = this.add
        .text(0, footerY, '', { fontFamily: FONT.family, fontSize: `${scaledFont(13, 11)}px`, color: hex(PALETTE.dim) })
        .setOrigin(0, 0.5);
      const modeIcon = this.add.graphics();
      const renderToggle = (): void => {
        const rival = this.aiEnabled;
        const col = rival ? PALETTE.ai : PALETTE.player;
        footerG.clear();
        footerG.fillStyle(PALETTE.surface, 0.6);
        footerG.fillRoundedRect(cx - footerW / 2, footerY - footerH / 2, footerW, footerH, footerH / 2);
        footerG.lineStyle(1.25, col, 0.8);
        footerG.strokeRoundedRect(cx - footerW / 2, footerY - footerH / 2, footerW, footerH, footerH / 2);
        modeMain.setText(rival ? 'MODE: RIVAL' : 'MODE: PRACTICE').setColor(hex(col));
        modeSub.setText(rival ? '  warmup first' : '  no rival');
        // Leading mode glyph (rival = sword, practice = chomper) in the mode accent, grouped with the
        // text and centered as a unit — matches the handoff mode pill (ref shows a magenta sword).
        const modeIconS = 14;
        const modeIconGap = 7;
        modeIcon.clear();
        drawIcon(modeIcon, rival ? 'sword' : 'chomp', modeIconS, col);
        const total = modeIconS + modeIconGap + modeMain.width + modeSub.width;
        const groupLeft = cx - total / 2;
        modeIcon.setPosition(groupLeft + modeIconS / 2, footerY);
        modeMain.setX(groupLeft + modeIconS + modeIconGap);
        modeSub.setX(groupLeft + modeIconS + modeIconGap + modeMain.width);
        // The seed-PLAY (classic_home) hero relabels with the rival toggle; the daily hero never does.
        if (!heroDaily) rebuildPrimary();
      };
      renderToggle();
      const footerHit = this.add.rectangle(cx, footerY, footerW, Math.max(footerH, 44), 0xffffff, 0.001).setInteractive({ useHandCursor: true });
      footerHit.on('pointerup', () => {
        this.aiEnabled = !this.aiEnabled;
        // Persist the explicit choice so a returning user's PRACTICE (or RIVAL) preference survives — the
        // never-chosen default stays RIVAL; only an explicit toggle writes here.
        this.writeModePref(this.aiEnabled);
        audio().playSfx('uiTap');
        renderToggle();
      });
      if (!land && H >= 760) {
        const retentionY = tallPortraitHome
          ? Math.min(H - 112, footerY + 94)
          : Math.min(H - 112, footerY + 86);
        this.buildMobileRetentionStrip(cx, colW, retentionY, stats, doneToday);
      }

      // (AI-disclosure link removed from the menu for the clean redline; it stays reachable via the
      //  'i' key, and the Store/Credits surfaces carry the AI-content disclosure for compliance.)

      // #39: the MODE (RIVAL/PRACTICE) footer is the third spotlight beat for a graduate.
      gradTargets.push({ x: cx, y: footerY, w: footerW, h: footerH, label: 'RIVAL' });

      // Keyboard: Enter plays the selected seed (when the input isn't focused — guarded in the handler).
      this.input.keyboard?.on('keydown-ENTER', () => {
        if (document.activeElement === this.inputEl) return; // input has its own Enter handler
        this.playSelectedSeed();
      });

      // #39: run the one-shot graduation spotlight AFTER the hub exists so it pulses the real affordances.
      if (justGraduated) this.runGraduationSpotlight(gradTargets);
    } // end returning-player discovery hub (P1.1)

    // Offline graceful-fallback callout: if the device reports no network on entry, surface the
    // dismissible offline banner (boards still play from the cached seed pack). Reactive listeners
    // show/hide it as connectivity changes; cleaned up on scene shutdown so nothing leaks.
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      this.offlineBanner = showOfflineBanner(this);
      this.openOfflineFallbackPanel();
    }
    if (typeof window !== 'undefined') {
      const onOffline = (): void => {
        if (this.scene.isActive() && !this.offlineBanner?.active) this.offlineBanner = showOfflineBanner(this);
        if (this.scene.isActive()) this.openOfflineFallbackPanel();
      };
      const onOnline = (): void => {
        this.offlineBanner?.destroy();
        this.offlineBanner = undefined;
        this.offlinePanelShown = false;
      };
      window.addEventListener('offline', onOffline);
      window.addEventListener('online', onOnline);
      this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
        window.removeEventListener('offline', onOffline);
        window.removeEventListener('online', onOnline);
      });
    }

    // ---- PROGRESSION: compact RANK chip + DAILY MISSIONS mini-list ----------
    // The rank chip (rank name + a thin XP bar) sits top-left, mirroring the top-right icons; tapping
    // it opens the Stats panel (which also lists today's missions). A restrained 3-row missions strip
    // sits just under the chip ONLY if there's clear vertical room above the topic-entry caption; on a
    // short phone we drop the inline strip (it's still in the Stats panel) so nothing crowds the hero.
    if (home.mode !== 'new') {
      // Rank + missions are returning-player meta — a brand-new player has neither yet (P1.1).
      if (!land) this.buildRankChip(W, H);
      this.buildMissionsBadge(W, H);
      // P2.5: announce any skin milestone crossed since the last visit (single celebration site).
      this.celebrateSkinUnlocks();
    }

    // Clean up the overlay no matter how we leave this scene.
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.teardownOverlay());
    this.events.once(Phaser.Scenes.Events.DESTROY, () => this.teardownOverlay());

    // M3 SHARE DEEP LINK: if we arrived from a "beat my score" link, guard cold users with the
    // tutorial first; returning users auto-launch after a tick so the loading overlay paints over a
    // fully-built menu. A TOPIC-LESS daily deep link (?play=1&daily=1) also enters here so it launches
    // playDaily → getDaily (the topic-of-day), matching the production DAILY-button flow.
    if (this.deepLinkTopic || this.deepLinkDaily) {
      const topic = this.deepLinkTopic;
      const daily = this.deepLinkDaily;
      this.deepLinkTopic = '';
      const launch = (): void => {
        if (!this.scene.isActive()) return;
        // P1.2: mark this as a deep-linked launch so the daily branch (playDaily, launchingKind
        // 'daily') still carries the challenge target/ghost/series — a Daily is the FAIREST head-to-head
        // (identical board for both players), so it must NOT drop the challenge payload (#3 fix).
        this.launchedFromDeepLink = true;
        // GHOST DUELS Slice A: a ?raceghost=1 deep-linked daily must carry the self-race intent through
        // playDaily (previously dropped → dead &raceghost=1); GameScene fails open if it can't pin.
        if (daily) this.playDaily({ raceGhost: boot.ghostSource === 'last-daily' });
        else this.playTopic(topic, 'deep_link');
      };
      if (!boot.debug && !onboardingSeen()) {
        this.openHowToPlay(launch);
      } else {
        this.time.delayedCall(60, launch);
      }
    }
  }

  /**
   * The composed character-in-arena hero shared by BOTH homes (#13/#17): a stronger arena grid, an
   * additive glow pool + grounding plinth, faint orbiting answer-tile chips it "hunts", and the unified
   * raster muncher (geometric decode fallback survives headless). Options: `rival` adds the BYT3 creature
   * in-frame (returning hub); `chomp` fires the one-shot first-open CHOMP+score pop (new-player home).
   */
  private buildArenaHero(cx: number, y: number, size: number, W: number, opts: { rival?: boolean; chomp?: boolean } = {}): void {
    this.buildHeaderArena(cx, y, Math.min(W * 0.86, 470), W, true);

    // Soft radial glow pool behind the character + a grounding plinth ellipse under its feet.
    const glow = this.add.graphics().setBlendMode(Phaser.BlendModes.ADD).setDepth(1);
    for (let i = 4; i >= 1; i--) {
      glow.fillStyle(PALETTE.player, 0.028 * i);
      glow.fillCircle(cx, y, size * 0.34 * i);
    }
    const plinth = this.add.graphics().setDepth(1);
    plinth.fillStyle(PALETTE.player, 0.1);
    plinth.fillEllipse(cx, y + size * 0.62, size * 1.5, size * 0.34);
    plinth.fillStyle(PALETTE.player, 0.16);
    plinth.fillEllipse(cx, y + size * 0.62, size * 1.05, size * 0.22);

    if (!opts.rival) this.buildHeroChips(cx, y, size, W);

    // The BYT3 rival shares the arena on the returning hub — the character you duel is on the home screen.
    if (opts.rival) {
      attachMascot(this, 'rival', cx + size * 0.78, y + size * 0.22, size * 0.66, { depth: 2 });
      this.add
        .text(cx - size * 0.08, y + size * 0.82, 'YOU', {
          fontFamily: FONT.display,
          fontSize: `${Math.max(10, Math.round(size * 0.1))}px`,
          color: hex(PALETTE.player),
          fontStyle: 'bold',
        })
        .setOrigin(0.5)
        .setDepth(5);
      this.add
        .text(cx + size * 0.78, y + size * 0.82, 'BYT3', {
          fontFamily: FONT.display,
          fontSize: `${Math.max(10, Math.round(size * 0.1))}px`,
          color: hex(PALETTE.ai),
          fontStyle: 'bold',
        })
        .setOrigin(0.5)
        .setDepth(5);
    }

    // Unified raster hero (attachMascot = the GameScene sheet/anim keys + geometric decode fallback).
    const player = attachMascot(this, 'player', cx, y, size, { skin: selectedCosmetic(), depth: 3 });
    if (motionAllowed()) {
      this.tweens.add({ targets: player.obj, angle: 3, duration: 2300, yoyo: true, repeat: -1, ease: 'Sine.easeInOut' });
    }
    if (opts.chomp) this.heroChompPop(cx, y, size, player.obj);
  }

  /**
   * #17 first-open payoff: the hero bites once (squash/stretch + munch SFX) and a "+100" score chip pops
   * off it — the new player sees the verb's reward before they've pressed anything. reduceMotion holds the
   * static composed frame (no bite, no pop, no sound-tied motion), matching the game's motion contract.
   */
  private heroChompPop(cx: number, y: number, size: number, spr: Phaser.GameObjects.GameObject): void {
    if (!motionAllowed()) return;
    this.time.delayedCall(560, () => {
      const s = spr as Phaser.GameObjects.Sprite;
      if (!s.active) return;
      const bx = s.scaleX, by = s.scaleY;
      this.tweens.add({ targets: s, scaleX: bx * 1.16, scaleY: by * 0.84, duration: 95, yoyo: true, ease: 'Quad.easeOut' });
      audio().playSfx('munch', { volume: 0.5 });
      const pop = this.add
        .text(cx + size * 0.24, y - size * 0.28, '+100', { fontFamily: FONT.display, fontSize: `${Math.round(size * 0.3)}px`, color: hex(PALETTE.correct), fontStyle: 'bold' })
        .setOrigin(0.5)
        .setDepth(6)
        .setScale(0.6)
        .setAlpha(0);
      this.tweens.add({ targets: pop, scale: 1, alpha: 1, y: y - size * 0.64, duration: 320, ease: 'Back.easeOut' });
      this.tweens.add({ targets: pop, alpha: 0, delay: 820, duration: 380, onComplete: () => pop.destroy() });
    });
  }

  /**
   * Faint orbiting answer-tile chips — the "world" the muncher hunts in: a plain answer ('42'), an
   * unknown ('?', scan), a committed CHOMP ('✓', correct). Chips passing "behind" the character
   * sink below its depth, shrink, and fade for pseudo-3D; static ring under reduced-motion. Orbit
   * extents are sized so the ring never reaches the wordmark above or the CTA below.
   */
  private buildHeroChips(cx: number, y: number, size: number, areaW: number): void {
    const defs = [
      { label: 'FACT', color: PALETTE.text, phase: 0 },
      { label: 'SCAN', color: PALETTE.scan, phase: (Math.PI * 2) / 3 },
      { label: '✓', color: PALETTE.correct, phase: (Math.PI * 4) / 3 },
    ];
    const land = this.scale.width > this.scale.height;
    const rx = land ? Math.min(size * 1.32, areaW * 0.23) : Math.min(size * 1.72, this.scale.width * 0.38);
    const ry = size * 0.52;
    const yBias = -size * 0.12;
    const chipW = Math.max(48, size * 0.62);
    const chipH = Math.max(22, size * 0.3);
    const fs = Math.round(chipH * 0.52);
    for (const def of defs) {
      const g = this.add.graphics();
      g.fillStyle(PALETTE.tile, 0.9);
      g.fillRoundedRect(-chipW / 2, -chipH / 2, chipW, chipH, 6);
      g.lineStyle(1, PALETTE.lineStrong, 0.9);
      g.strokeRoundedRect(-chipW / 2, -chipH / 2, chipW, chipH, 6);
      const t = this.add
        .text(0, 0, def.label, { fontFamily: FONT.display, fontSize: `${fs}px`, color: hex(def.color), fontStyle: 'bold' })
        .setOrigin(0.5);
      const c = this.add.container(0, 0, [g, t]);
      const place = (a: number): void => {
        c.setPosition(cx + Math.cos(a) * rx, y + yBias + Math.sin(a) * ry);
        const front = (Math.sin(a) + 1) / 2; // 1 = nearest (bottom of the ellipse), 0 = farthest
        c.setDepth(land ? 5 : front < 0.5 ? 2 : 4);
        c.setScale(0.82 + 0.18 * front);
        c.setAlpha(0.5 + 0.35 * front);
      };
      place(def.phase);
      if (motionAllowed()) {
        const proxy = { a: def.phase };
        this.tweens.add({
          targets: proxy,
          a: def.phase + Math.PI * 2,
          duration: 14000,
          repeat: -1,
          onUpdate: () => place(proxy.a),
        });
      }
    }
  }

  /** Primary CTA: a full-width green ctaButton. The returning-hub Daily CTA adds a thin gold ring;
   *  the new-player START TRAINING is a clean green button (no ring) per the redline. */
  private buildPrimaryCta(
    cx: number,
    y: number,
    w: number,
    h: number,
    label: string,
    iconName: IconName,
    onClick: () => void,
    gold = true,
  ): Phaser.GameObjects.Container {
    const els: Phaser.GameObjects.GameObject[] = [];
    if (gold) {
      const ring = this.add.graphics();
      ring.lineStyle(2, PALETTE.gold, 0.85);
      ring.strokeRoundedRect(cx - w / 2 - 3, y - h / 2 - 3, w + 6, h + 6, 16);
      els.push(ring);
    }
    const fontPx = Math.max(15, Math.min(20, w * 0.06));
    els.push(ctaButton(this, cx, y, label, onClick, { width: w, height: h, icon: iconName, glow: gold ? PALETTE.gold : undefined, fontPx }));
    return this.add.container(0, 0, els);
  }

  /** Portrait returning-home retention strip: live missions + streak chest in the lower empty band. */
  private buildMobileRetentionStrip(cx: number, colW: number, y: number, stats: ReturnType<typeof getStats>, doneToday: boolean): void {
    const missions: MissionView[] = getDailyMissions(stats).slice(0, 3);
    if (missions.length === 0) return;
    const depth = 3;
    const fitText = (txt: Phaser.GameObjects.Text, maxW: number, minScale = 0.72): void => {
      if (txt.width > maxW) txt.setScale(Math.max(minScale, maxW / txt.width));
    };
    const shortMission = (s: string): string => {
      const l = s.toLowerCase();
      if (l.includes('daily') && l.includes('streak')) return 'Streak';
      if (l.includes('win') && l.includes('board')) return 'Win';
      if (l.includes('play') && l.includes('new')) return 'New topic';
      if (l.includes('play') && l.includes('board')) return 'Boards';
      if (l.includes('combo')) return 'Combo';
      if (l.includes('gauntlet') || l.includes('thread')) return 'Thread';
      if (l.includes('score')) return 'Score';
      if (l.includes('scan')) return 'Scans';
      return s.length > 9 ? `${s.slice(0, 8).trim()}...` : s;
    };
    const addHit = (x: number, yy: number, w: number, h: number, cb: () => void): void => {
      this.add
        .rectangle(x, yy, w, h, 0xffffff, 0.001)
        .setDepth(depth + 3)
        .setInteractive({ useHandCursor: true })
        .on('pointerup', () => {
          audio().playSfx('uiTap');
          this.hideTopicInput();
          cb();
        });
    };

    const doneCount = missions.filter((m) => m.done).length;
    const stripTop = y - 48;
    labelText(this, cx - colW / 2, stripTop, `Today goals ${doneCount}/${missions.length}`, { size: 11, color: PALETTE.dim }).setDepth(depth + 1);

    const gap = 8;
    const chipW = (colW - gap * 2) / 3;
    const chipH = 66;
    const missionY = y;
    missions.forEach((m, i) => {
      const x = cx - colW / 2 + chipW / 2 + i * (chipW + gap);
      const pct = m.target > 0 ? Phaser.Math.Clamp(m.cur / m.target, 0, 1) : 0;
      const color = m.done ? PALETTE.correct : PALETTE.scan;
      cardPanel(this, x, missionY, chipW, chipH, { fill: PALETTE.surface, border: color, accent: color, radius: 10, borderWeight: 1.15 }).setDepth(depth);
      icon(this, m.done ? 'check' : 'target', x - chipW / 2 + 20, missionY - 8, 17, color).setDepth(depth + 1);
      const value = this.add
        .text(x + chipW / 2 - 10, missionY - 8, `${m.cur}/${m.target}`, {
          fontFamily: FONT.display,
          fontSize: `${scaledFont(14, 11)}px`,
          color: hex(color),
          fontStyle: 'bold',
        })
        .setOrigin(1, 0.5)
        .setDepth(depth + 1);
      const label = this.add
        .text(x - chipW / 2 + 38, missionY - 8, shortMission(m.label), {
          fontFamily: FONT.display,
          fontSize: `${scaledFont(13, 10)}px`,
          color: hex(PALETTE.text),
          fontStyle: '600',
        })
        .setOrigin(0, 0.5)
        .setDepth(depth + 1);
      fitText(label, chipW - 54 - value.width, 0.68);
      progressBar(this, x - chipW / 2 + 16, missionY + 20, chipW - 32, 5, pct, color).container.setDepth(depth + 1);
      addHit(x, missionY, chipW, Math.max(chipH, 44), () => openStats(this));
    });

    const chestY = missionY + 76;
    const streak = liveDailyStreak(stats);
    const chestColor = doneToday ? PALETTE.correct : PALETTE.armed;
    cardPanel(this, cx, chestY, colW, 46, { fill: PALETTE.surface, border: chestColor, accent: chestColor, radius: 12, borderWeight: 1.15 }).setDepth(depth);
    icon(this, doneToday ? 'checkCircle' : 'flame', cx - colW / 2 + 24, chestY, 18, chestColor).setDepth(depth + 1);
    const title = this.add
      .text(cx - colW / 2 + 48, chestY - 8, doneToday ? 'STREAK CHEST CLAIMED' : streak > 0 ? `${streak} DAY STREAK CHEST` : 'STREAK CHEST', {
        fontFamily: FONT.display,
        fontSize: `${scaledFont(13, 11)}px`,
        color: hex(chestColor),
        fontStyle: 'bold',
      })
      .setOrigin(0, 0.5)
      .setDepth(depth + 1);
    fitText(title, colW - 74, 0.72);
    const sub = this.add
      .text(cx - colW / 2 + 48, chestY + 10, doneToday ? 'Come back tomorrow' : 'Clear Daily to fill it', {
        fontFamily: FONT.family,
        fontSize: `${scaledFont(11, 10)}px`,
        color: hex(PALETTE.dim),
        fontStyle: '600',
      })
      .setOrigin(0, 0.5)
      .setDepth(depth + 1);
    fitText(sub, colW - 74, 0.72);
    addHit(cx, chestY, colW, Math.max(46, 44), () => openTodayGauntlet(this, { onPlay: (o) => this.playDaily(o) }));
    const weeklyY = chestY + 58;
    cardPanel(this, cx, weeklyY, colW, 42, { fill: PALETTE.surface, border: PALETTE.scan, accent: PALETTE.scan, radius: 12, borderWeight: 1 }).setDepth(depth);
    icon(this, 'target', cx - colW / 2 + 24, weeklyY, 17, PALETTE.scan).setDepth(depth + 1);
    const weeklyTitle = this.add
      .text(cx - colW / 2 + 48, weeklyY - 7, 'WEEKLY CHALLENGE', {
        fontFamily: FONT.display,
        fontSize: `${scaledFont(12, 10)}px`,
        color: hex(PALETTE.scan),
        fontStyle: 'bold',
      })
      .setOrigin(0, 0.5)
      .setDepth(depth + 1);
    fitText(weeklyTitle, colW - 74, 0.72);
    const weeklySub = this.add
      .text(cx - colW / 2 + 48, weeklyY + 9, '3-board streak ladder', {
        fontFamily: FONT.family,
        fontSize: `${scaledFont(10.5, 9)}px`,
        color: hex(PALETTE.dim),
        fontStyle: '600',
      })
      .setOrigin(0, 0.5)
      .setDepth(depth + 1);
    fitText(weeklySub, colW - 74, 0.72);
    addHit(cx, weeklyY, colW, 44, () => openStats(this));
  }

  /** Desktop-only support rails from the July 5 redline: progress/boards at left, streak/news at right. */
  private buildDesktopHomeRails(W: number, H: number, stats: ReturnType<typeof getStats>, doneToday: boolean): void {
    const railW = Math.max(176, Math.min(204, W * 0.155));
    const leftX = Math.round(Math.max(railW / 2 + 18, W * 0.088));
    const rightX = Math.round(W - railW / 2 - 18);
    const cardDepth = 2;
    const textDepth = cardDepth + 1;
    const compact = (s: string, n: number): string => (s.length > n ? `${s.slice(0, Math.max(0, n - 3)).trim()}...` : s);
    const missionShort = (s: string): string => {
      const l = s.toLowerCase();
      if (l.includes('daily') && l.includes('streak')) return 'Keep streak';
      if (l.includes('win') && l.includes('board')) return 'Win boards';
      if (l.includes('combo')) return 'Hit combo';
      if (l.includes('gauntlet') || l.includes('thread')) return 'Thread run';
      if (l.includes('score')) return 'Score';
      if (l.includes('new') && l.includes('topic')) return 'New topic';
      if (l.includes('scan')) return 'Use scans';
      return compact(s, 13);
    };
    const fitText = (txt: Phaser.GameObjects.Text, maxW: number, minScale = 0.72): void => {
      if (txt.width > maxW) txt.setScale(Math.max(minScale, maxW / txt.width));
    };
    const railLabel = (x: number, y: number, text: string): Phaser.GameObjects.Text => {
      const t = labelText(this, x, y, text, { size: 11 }).setDepth(textDepth);
      fitText(t, railW - 36, 0.76);
      return t;
    };
    const addHit = (x: number, y: number, w: number, h: number, cb: () => void): void => {
      this.add
        .rectangle(x, y, w, h, 0xffffff, 0.001)
        .setDepth(textDepth + 1)
        .setInteractive({ useHandCursor: true })
        .on('pointerup', () => {
          audio().playSfx('uiTap');
          this.hideTopicInput();
          cb();
        });
    };

    const progress = getProgress(stats);
    const progressY = Math.round(H * 0.195);
    cardPanel(this, leftX, progressY, railW, 116, { fill: PALETTE.surface, border: PALETTE.player, accent: PALETTE.player, radius: 12 })
      .setDepth(cardDepth);
    railLabel(leftX - railW / 2 + 18, progressY - 42, 'Progress');
    icon(this, 'crown', leftX - railW / 2 + 24, progressY - 12, 18, PALETTE.player).setDepth(textDepth);
    this.add
      .text(leftX - railW / 2 + 48, progressY - 18, `LV ${progress.rank + 1} ${progress.rankName}`.toUpperCase(), {
        fontFamily: FONT.display,
        fontSize: `${scaledFont(15, 12)}px`,
        color: hex(PALETTE.player),
        fontStyle: 'bold',
      })
      .setOrigin(0, 0.5)
      .setDepth(textDepth);
    this.add
      .text(leftX - railW / 2 + 48, progressY + 2, progress.maxed ? 'Max rank' : `${progress.xp}/${progress.nextAt} XP`, {
        fontFamily: FONT.family,
        fontSize: `${scaledFont(11, 11)}px`,
        color: hex(PALETTE.dim),
      })
      .setOrigin(0, 0.5)
      .setDepth(textDepth);
    progressBar(this, leftX - railW / 2 + 18, progressY + 34, railW - 36, 8, progress.pctToNext, PALETTE.player).container.setDepth(textDepth);
    addHit(leftX, progressY, railW, 116, () => openStats(this));

    const boardsY = Math.round(H * 0.415);
    const boardsH = 214;
    cardPanel(this, leftX, boardsY, railW, boardsH, { fill: PALETTE.surface, border: PALETTE.lineStrong, accent: PALETTE.gold, radius: 12 })
      .setDepth(cardDepth);
    railLabel(leftX - railW / 2 + 18, boardsY - boardsH / 2 + 22, 'Board by board');
    const boardRows = [
      { iconName: 'play' as IconName, color: PALETTE.correct, label: 'Warmup', sub: stats.gamesPlayed > 0 ? 'Recent route known' : 'First bite ready', value: stats.gamesPlayed > 0 ? 'CLEAR' : 'READY' },
      { iconName: 'sword' as IconName, color: PALETTE.ai, label: 'Rival', sub: 'BYT3 pressure', value: this.aiEnabled ? 'ON' : 'OFF' },
      { iconName: 'flame' as IconName, color: PALETTE.armed, label: 'Gauntlet', sub: 'Daily chain', value: doneToday ? 'DONE' : 'LIVE' },
    ];
    boardRows.forEach((row, i) => {
      const y = boardsY - 54 + i * 56;
      const rowW = railW - 28;
      const rowH = 50;
      const rowG = this.add.graphics().setDepth(textDepth);
      rowG.fillStyle(PALETTE.tile, 1);
      rowG.fillRoundedRect(leftX - rowW / 2, y - rowH / 2, rowW, rowH, 8);
      rowG.lineStyle(1, PALETTE.line, 1);
      rowG.strokeRoundedRect(leftX - rowW / 2, y - rowH / 2, rowW, rowH, 8);
      icon(this, row.iconName, leftX - rowW / 2 + 22, y - 8, 18, row.color).setDepth(textDepth);
      const value = this.add
        .text(leftX + rowW / 2 - 12, y - 10, row.value, {
          fontFamily: FONT.display,
          fontSize: `${scaledFont(12, 11)}px`,
          color: hex(row.color),
          fontStyle: 'bold',
        })
        .setOrigin(1, 0.5)
        .setDepth(textDepth);
      const label = this.add
        .text(leftX - rowW / 2 + 44, y - 10, row.label, {
          fontFamily: FONT.family,
          fontSize: `${scaledFont(12, 11)}px`,
          color: hex(PALETTE.text),
          fontStyle: '600',
        })
        .setOrigin(0, 0.5)
        .setDepth(textDepth);
      fitText(label, rowW - 70 - value.width, 0.72);
      const sub = this.add
        .text(leftX - rowW / 2 + 44, y + 12, row.sub, {
          fontFamily: FONT.family,
          fontSize: `${scaledFont(10, 10)}px`,
          color: hex(PALETTE.dim),
          fontStyle: '600',
        })
        .setOrigin(0, 0.5)
        .setDepth(textDepth);
      fitText(sub, rowW - 58, 0.7);
    });
    addHit(leftX, boardsY + 2, railW, boardsH, () => openTodayGauntlet(this, { onPlay: (o) => this.playDaily(o) }));

    const bestY = Math.round(H * 0.665);
    cardPanel(this, leftX, bestY, railW, 110, { fill: PALETTE.surface, border: PALETTE.line, accent: PALETTE.correct, radius: 12 }).setDepth(cardDepth);
    railLabel(leftX - railW / 2 + 18, bestY - 38, 'Career');
    [
      { label: 'BEST', value: String(stats.bestScore), color: PALETTE.correct },
      { label: 'STREAK', value: `${stats.winStreak}`, color: PALETTE.gold },
      { label: 'THREADS', value: `${stats.gauntletsThreaded}`, color: PALETTE.scan },
    ].forEach((item, i) => {
      const colW = (railW - 36) / 3;
      const x = leftX - railW / 2 + 18 + colW * (i + 0.5);
      this.add
        .text(x, bestY - 4, item.value, { fontFamily: FONT.display, fontSize: `${scaledFont(18, 13)}px`, color: hex(item.color), fontStyle: 'bold' })
        .setOrigin(0.5)
        .setDepth(textDepth);
      const statLabel = labelText(this, x, bestY + 21, item.label, { size: 8, origin: 0.5 }).setDepth(textDepth);
      fitText(statLabel, colW - 6, 0.7);
    });

    const streakY = Math.round(H * 0.18);
    cardPanel(this, rightX, streakY, railW, 98, { fill: PALETTE.surface, border: PALETTE.armed, accent: PALETTE.armed, radius: 12 }).setDepth(cardDepth);
    railLabel(rightX - railW / 2 + 18, streakY - 32, 'Streak chest');
    icon(this, doneToday ? 'checkCircle' : 'flame', rightX - railW / 2 + 26, streakY - 2, 20, doneToday ? PALETTE.correct : PALETTE.armed).setDepth(textDepth);
    const streakTitle = this.add
      .text(rightX - railW / 2 + 54, streakY - 10, doneToday ? 'Chest claimed' : liveDailyStreak(stats) > 0 ? `${liveDailyStreak(stats)} day streak` : 'No streak yet', {
        fontFamily: FONT.display,
        fontSize: `${scaledFont(15, 12)}px`,
        color: hex(doneToday ? PALETTE.correct : PALETTE.armed),
        fontStyle: 'bold',
      })
      .setOrigin(0, 0.5)
      .setDepth(textDepth);
    fitText(streakTitle, railW - 66, 0.72);
    const streakSub = this.add
      .text(rightX - railW / 2 + 54, streakY + 12, doneToday ? 'Come back tomorrow' : 'Clear Daily to fill', {
        fontFamily: FONT.family,
        fontSize: `${scaledFont(12, 11)}px`,
        color: hex(PALETTE.dim),
      })
      .setOrigin(0, 0.5)
      .setDepth(textDepth);
    fitText(streakSub, railW - 66, 0.72);
    const streakDays = Math.max(0, Math.min(5, liveDailyStreak(stats)));
    for (let i = 0; i < 5; i += 1) {
      const bx = rightX - railW / 2 + 54 + i * 18;
      const by = streakY + 32;
      const box = this.add.graphics().setDepth(textDepth);
      box.fillStyle(i < streakDays || doneToday ? PALETTE.armed : PALETTE.tile, i < streakDays || doneToday ? 1 : 0.9);
      box.fillRoundedRect(bx, by, 13, 8, 3);
      box.lineStyle(1, PALETTE.line, 0.75);
      box.strokeRoundedRect(bx, by, 13, 8, 3);
    }
    addHit(rightX, streakY, railW, 98, () => openTodayGauntlet(this, { onPlay: (o) => this.playDaily(o) }));

    const missionY = Math.round(H * 0.395);
    const missionH = 226;
    cardPanel(this, rightX, missionY, railW, missionH, { fill: PALETTE.surface, border: PALETTE.lineStrong, accent: PALETTE.scan, radius: 12 }).setDepth(cardDepth);
    railLabel(rightX - railW / 2 + 18, missionY - missionH / 2 + 22, 'Missions');
    getDailyMissions(stats).slice(0, 3).forEach((m, i) => {
      const pct = m.target > 0 ? Math.max(0, Math.min(1, m.cur / m.target)) : 0;
      const y = missionY - 62 + i * 56;
      const rowW = railW - 28;
      const rowH = 40;
      const rowG = this.add.graphics().setDepth(textDepth);
      rowG.fillStyle(PALETTE.tile, 1);
      rowG.fillRoundedRect(rightX - rowW / 2, y - rowH / 2, rowW, rowH, 8);
      rowG.lineStyle(1, PALETTE.line, 1);
      rowG.strokeRoundedRect(rightX - rowW / 2, y - rowH / 2, rowW, rowH, 8);
      icon(this, m.done ? 'check' : 'target', rightX - rowW / 2 + 22, y, 18, m.done ? PALETTE.correct : PALETTE.scan).setDepth(textDepth);
      const value = this.add
        .text(rightX + rowW / 2 - 12, y, `${m.cur}/${m.target}`, {
          fontFamily: FONT.display,
          fontSize: `${scaledFont(13, 11)}px`,
          color: hex(m.done ? PALETTE.correct : PALETTE.scan),
          fontStyle: 'bold',
        })
        .setOrigin(1, 0.5)
        .setDepth(textDepth);
      const label = this.add
        .text(rightX - rowW / 2 + 46, y, missionShort(m.label), {
          fontFamily: FONT.family,
          fontSize: `${scaledFont(12, 11)}px`,
          color: hex(PALETTE.text),
          fontStyle: '600',
        })
        .setOrigin(0, 0.5)
        .setDepth(textDepth);
      const labelRoom = rowW - 68 - value.width;
      if (label.width > labelRoom) label.setScale(Math.max(0.78, labelRoom / label.width));
      progressBar(this, rightX - railW / 2 + 24, y + 28, railW - 48, 5, pct, m.done ? PALETTE.correct : PALETTE.scan).container.setDepth(textDepth);
    });
    addHit(rightX, missionY, railW, missionH, () => openStats(this));

    const newsY = Math.round(H * 0.665);
    cardPanel(this, rightX, newsY, railW, 112, { fill: PALETTE.surface, border: PALETTE.line, accent: PALETTE.ai, radius: 12 }).setDepth(cardDepth);
    railLabel(rightX - railW / 2 + 18, newsY - 38, 'Weekly challenge');
    const news = [
      { iconName: 'trophy' as IconName, label: `Best combo ${stats.bestCombo}`, color: PALETTE.gold },
      { iconName: 'target' as IconName, label: `Threaded ${stats.gauntletsThreaded}`, color: PALETTE.scan },
    ];
    news.forEach((row, i) => {
      const y = newsY - 8 + i * 34;
      icon(this, row.iconName, rightX - railW / 2 + 26, y, 17, row.color).setDepth(textDepth);
      this.add
        .text(rightX - railW / 2 + 50, y, row.label, {
          fontFamily: FONT.family,
          fontSize: `${scaledFont(12, 11)}px`,
          color: hex(PALETTE.text),
          fontStyle: '600',
        })
        .setOrigin(0, 0.5)
        .setDepth(textDepth);
    });
  }

  /** #54 — map a seed slug's CATEGORY (the `cat::` prefix) to a quick-pick glyph + accent color, so each
   *  chip reads as a distinct toy. Unknown categories fall back to a neutral scan sparkle. */
  private categoryGlyph(normalized: string): { icon: IconName; color: number } {
    const cat = normalized.split('::')[0];
    const map: Record<string, { icon: IconName; color: number }> = {
      mythology: { icon: 'crown', color: PALETTE.gold },
      astronomy: { icon: 'star', color: PALETTE.scan },
      space: { icon: 'star', color: PALETTE.scan },
      science: { icon: 'bolt', color: PALETTE.scan },
      nature: { icon: 'sparkle', color: PALETTE.correct },
      animals: { icon: 'sparkle', color: PALETTE.correct },
      biology: { icon: 'sparkle', color: PALETTE.correct },
      geography: { icon: 'target', color: PALETTE.armed },
      history: { icon: 'shield', color: PALETTE.armed },
      music: { icon: 'sparkle', color: PALETTE.ai },
      sports: { icon: 'target', color: PALETTE.correct },
      movies: { icon: 'star', color: PALETTE.gold },
      film: { icon: 'star', color: PALETTE.gold },
      food: { icon: 'flame', color: PALETTE.armed },
    };
    return map[cat] ?? { icon: 'sparkle', color: PALETTE.scan };
  }

  /** Shorten a seed-topic display string to 1–2 words for the compact quick-pick chips. */
  /**
   * #39 GRADUATION SPOTLIGHT — a one-shot "you're ready, here's what's next" sweep that pulses the three
   * discovery affordances (BUILD / DAILY / RIVAL) in place, armed by ResultScene's justGraduated handshake.
   * Non-blocking (no input veil): the graduate's first tap both dismisses the sweep AND activates whatever
   * they tapped. Motion-gated: reduced-motion draws all three static highlight rings + the banner and holds
   * (no moving sweep), fading on the same first-input / auto-timeout. Idempotent teardown.
   */
  private runGraduationSpotlight(targets: { x: number; y: number; w: number; h: number; label: string }[]): void {
    if (targets.length === 0) return;
    const reduced = !motionAllowed();
    const W = this.cameras.main.width;
    const H = this.cameras.main.height;
    const layer = this.add.container(0, 0).setDepth(20_000);

    // A concise header naming the beat, high above the hero so it never covers an affordance.
    // S9 FIX-C (G0 AMATEUR 1): H*0.045 landed the banner ON the top chrome row on desktop — the 0/3
    // missions pill's outline cut through "PICK YOUR" (mutual occlusion, CONFIRM crop). Seat it just
    // BELOW the top row's real bottom edge instead (same size/row formula buildRankChip and
    // buildMissionsBadge use), so the header never shares pixels with hub chrome on either viewport.
    const banner = this.add
      .text(W / 2, 0, "YOU'RE READY — PICK YOUR NEXT MATCH", {
        fontFamily: FONT.display,
        fontSize: `${scaledFont(15, 12)}px`,
        color: hex(PALETTE.armed),
        fontStyle: 'bold',
        align: 'center',
        stroke: '#0a0b12',
        strokeThickness: 3,
        wordWrap: { width: W * 0.9 },
      })
      .setOrigin(0.5);
    const topRowS = Math.max(34, Math.min(42, W * 0.06)); // rank-chip / missions-pill size formula
    const topRowBottom = Math.max(30, topRowS * 0.72) + topRowS / 2;
    banner.setY(Math.max(H * 0.045, topRowBottom + 12 + banner.height / 2));
    banner.setShadow(0, 2, '#000000', 6, false, true);
    layer.add(banner);

    const rings = targets.map((t) => {
      const g = this.add.graphics();
      g.lineStyle(3, PALETTE.armed, 1);
      g.strokeRoundedRect(t.x - t.w / 2 - 7, t.y - t.h / 2 - 7, t.w + 14, t.h + 14, 12);
      layer.add(g);
      return g;
    });

    let done = false;
    const teardown = (): void => {
      if (done) return;
      done = true;
      if (!this.scene.isActive()) return;
      this.tweens.killTweensOf(rings);
      this.tweens.killTweensOf(banner);
      if (reduced) {
        layer.destroy();
        return;
      }
      this.tweens.add({ targets: layer, alpha: 0, duration: 220, onComplete: () => layer.destroy() });
    };
    // Dismiss on the graduate's first input (non-blocking — the same tap still hits a button underneath),
    // or auto-dismiss after the ~2s sweep completes.
    this.input.once('pointerdown', teardown);
    this.input.keyboard?.once('keydown', teardown);
    this.time.delayedCall(reduced ? 2600 : 2400, teardown);

    if (reduced) {
      // Static: all three rings + banner held (no moving sweep); teardown fades/destroys on input/timeout.
      return;
    }
    // Sweep: pulse each ring in sequence so attention travels BUILD → DAILY → RIVAL.
    rings.forEach((g, i) => {
      g.setAlpha(0.15);
      this.tweens.add({
        targets: g,
        alpha: 1,
        duration: 240,
        delay: i * 560,
        hold: 300,
        yoyo: true,
        repeat: 1,
        ease: 'Sine.easeInOut',
      });
    });
    // A gentle banner breathe while the sweep runs.
    this.tweens.add({ targets: banner, alpha: { from: 0.6, to: 1 }, duration: 500, yoyo: true, repeat: 2, ease: 'Sine.easeInOut' });
  }

  private shortLabel(raw: string): string {
    const cleaned = raw.replace(/^\s*\d{4}\s+/, '').trim();
    const words = cleaned.split(/\s+/);
    let s = words.slice(0, 2).join(' ');
    if (s.length > 11 && words.length > 1) s = words[0];
    return s;
  }

  /** Lay a single centered line built from colored segments (e.g. emphasize CHOMP in `correct`). */
  private segLine(cx: number, y: number, parts: Array<{ t: string; color: number; bold?: boolean }>, fontPx: number): void {
    const objs = parts.map((p) =>
      this.add
        .text(0, y, p.t, { fontFamily: FONT.family, fontSize: `${fontPx}px`, color: hex(p.color), fontStyle: p.bold ? 'bold' : 'normal' })
        .setOrigin(0, 0.5),
    );
    const total = objs.reduce((a, o) => a + o.width, 0);
    let x = cx - total / 2;
    for (const o of objs) {
      o.setX(x);
      x += o.width;
    }
  }

  // `strong` = the new-player hero stage: same geometry, but alphas lifted so the playfield
  // actually reads behind the character (the 0.18/0.16 returning-hub whisper is invisible on OLED).
  private buildHeaderArena(cx: number, y: number, rowW: number, W: number, strong = false): void {
    const gridW = Math.min(rowW * 0.78, 340);
    const cols = 6;
    const rows = 3;
    const gap = Math.max(3, W * 0.006);
    const tile = Math.min(30, (gridW - gap * (cols - 1)) / cols);
    const gridH = rows * tile + (rows - 1) * gap;
    const startX = cx - (cols * tile + (cols - 1) * gap) / 2 + tile / 2;
    const startY = y - gridH / 2 + tile / 2;
    for (let row = 0; row < rows; row += 1) {
      for (let col = 0; col < cols; col += 1) {
        const x = startX + col * (tile + gap);
        const yy = startY + row * (tile + gap);
        const edge = row === 1 && (col === 0 || col === cols - 1);
        this.add
          .rectangle(x, yy, tile, tile, edge ? PALETTE.tileHot : PALETTE.tile, strong ? 0.65 : 0.18)
          .setStrokeStyle(1, edge ? PALETTE.scan : PALETTE.tileBorder, edge ? (strong ? 0.5 : 0.28) : strong ? 0.4 : 0.16);
      }
    }
    const route = this.add.graphics();
    route.lineStyle(3, PALETTE.scan, strong ? 0.38 : 0.24);
    route.beginPath();
    route.moveTo(startX, y);
    route.lineTo(cx - tile * 0.5, y);
    route.lineTo(cx + tile * 0.5, y + tile * 0.55);
    route.lineTo(startX + (cols - 1) * (tile + gap), y + tile * 0.55);
    route.strokePath();
    // The live hero/rival sprites (buildMenuHero) now stand on this faint playfield; the old
    // procedural circle/triangle route actors are retired so the header reads as one diorama.
  }

  /** G11: open (or replay) the how-to-play tutorial overlay. Used on first run + the menu/pause button. */
  private openHowToPlay(afterDone?: () => void): void {
    if (this.onboarding || this.loading) return;
    this.onboarding = true;
    // Hide the DOM topic-input overlay NOW (before the next 120ms position-sync) so its placeholder
    // can't bleed through the tutorial veil; the sync re-shows it once onboarding clears below.
    if (this.inputEl) this.inputEl.style.display = 'none';
    openOnboarding(this, () => {
      this.onboarding = false;
      if (this.inputEl && !this.loading) this.inputEl.style.display = 'block';
      afterDone?.();
    });
  }

  // --- P1.1 new-player home -------------------------------------------------
  /**
   * The brand-new player home: just the hero muncher (already drawn) + a short pitch for the training
   * run + a teaser of what unlocks. No topic entry / surprise / daily / recents / quick-picks / rival
   * toggle / rank / missions — the single START TRAINING hero is the only decision. Everything else
   * appears once `markOnboardingSeen()` flips after the final training board (see ResultScene).
   */
  private buildNewPlayerHome(cx: number, sizeW: number, colW: number, H: number, ctaY: number, ctaH: number, home: HomeModel): void {
    const land = this.scale.width > this.scale.height;
    const top = ctaY + ctaH / 2 + Math.max(34, H * 0.05);
    // Font + wrap track the ACTION-COLUMN basis (sizeW/colW), NOT the full design width — otherwise the
    // landscape 1280 stage balloons this block wider than the CTA and it spills toward the hero pane.
    // Portrait passes sizeW == W so the sizing is byte-identical there; colW just caps overrun.
    const fs = scaledFont(land ? 18 : Math.max(13, sizeW * 0.026), 12);
    const pitchTitle = land ? 'Learn the one move:' : 'A quick run teaches the one move:';
    const pitchLine = land
      ? [
          { t: 'Step onto answers. ', color: PALETTE.textDim },
          { t: 'CHOMP', color: PALETTE.correct, bold: true },
          { t: ' when sure.', color: PALETTE.textDim },
        ]
      : [
          { t: 'step onto an answer, then ', color: PALETTE.textDim },
          { t: 'CHOMP', color: PALETTE.correct, bold: true },
          { t: ' when you’re sure.', color: PALETTE.textDim },
        ];
    // Pitch: the one move, with CHOMP emphasized in `correct`.
    this.add
      .text(cx, top, pitchTitle, { fontFamily: FONT.family, fontSize: `${fs}px`, color: hex(PALETTE.text), align: 'center', wordWrap: { width: colW } })
      .setOrigin(0.5, 0);
    this.segLine(
      cx,
      top + Math.max(24, fs * 1.7),
      pitchLine,
      fs,
    );
    // Locked row: what unlocks after training (lock icon + label). Use `dim` not `mute` so the hint
    // clears WCAG-AA on the OLED field — `mute` (#5C6373) was effectively invisible.
    const lockY = top + Math.max(76, H * 0.105);
    const lockTxt = this.add
      .text(0, lockY, 'Topics · Daily · Rival unlock after training', { fontFamily: FONT.family, fontSize: `${scaledFont(land ? 13 : Math.max(11, sizeW * 0.021), 11)}px`, color: hex(PALETTE.textDim) })
      .setOrigin(0, 0.5);
    const lockS = 15;
    const totalW = lockS + 6 + lockTxt.width;
    const lx = cx - totalW / 2;
    icon(this, 'lock', lx + lockS / 2, lockY, lockS, PALETTE.dim);
    lockTxt.setX(lx + lockS + 6);
    if (home.recommendedTopic) {
      this.add
        .text(cx, lockY + Math.max(28, H * 0.045), `Up next: ${home.recommendedTopic.label}`, { fontFamily: FONT.family, fontSize: `${scaledFont(land ? 12.5 : Math.max(11, sizeW * 0.02), 11)}px`, color: hex(PALETTE.scan), align: 'center', wordWrap: { width: colW } })
        .setOrigin(0.5, 0);
    }
    const trustY = land ? Math.min(H - 98, lockY + Math.max(80, H * 0.112)) : Math.min(H - 90, lockY + Math.max(128, H * 0.15));
    const trustW = Math.min(colW, land ? 380 : 330);
    const trustH = land ? 48 : 50;
    const trust = this.add.graphics();
    trust.fillStyle(PALETTE.surface, 0.86);
    trust.fillRoundedRect(cx - trustW / 2, trustY - trustH / 2, trustW, trustH, 12);
    trust.lineStyle(1.25, PALETTE.correct, 0.75);
    trust.strokeRoundedRect(cx - trustW / 2, trustY - trustH / 2, trustW, trustH, 12);
    icon(this, 'shield', cx - trustW / 2 + 28, trustY, 24, PALETTE.correct);
    const trustText = this.add
      .text(cx - trustW / 2 + 56, trustY, 'AI-written · independently verified', {
        fontFamily: FONT.family,
        fontSize: `${scaledFont(land ? 12.5 : 11, 11)}px`,
        color: hex(PALETTE.text),
        fontStyle: '600',
      })
      .setOrigin(0, 0.5);
    const maxTrustTextW = trustW - 78;
    if (trustText.width > maxTrustTextW) trustText.setScale(Math.max(0.78, maxTrustTextW / trustText.width));
  }

  // --- top-right utility buttons --------------------------------------------
  /** Mode-aware geometric icon cluster (returning = skins · stats · settings, new = help · settings). */
  private buildTopRightButtons(W: number, mode: 'new' | 'returning'): void {
    const s = Math.max(34, Math.min(42, W * 0.06));
    const gap = 8;
    const y = Math.max(30, s * 0.72);
    // Index 0 is the rightmost chip; the array reads right-to-left in screen order.
    const cluster: Array<[IconName, () => void]> =
      mode === 'new'
        ? [
            ['gear', () => openSettings(this)],
            ['question', () => this.openHowToPlay()],
          ]
        : [
            ['gear', () => openSettings(this)],
            ['chart', () => openStats(this)],
            ['palette', () => openLocker(this)],
          ];
    cluster.forEach(([name, fn], i) => {
      const x = W - s / 2 - 12 - i * (s + gap);
      iconButton(
        this,
        x,
        y,
        name,
        () => {
          audio().unlock();
          this.hideTopicInput();
          fn();
        },
        { size: s },
      ).setDepth(9_000);
    });

    // Keyboard parity for the icon-only buttons (touch users tap; keyboard users press a key).
    // Guarded so a typed topic isn't hijacked: ignore when the text input has focus. ',' is the
    // conventional "settings" shortcut; 'T' opens sTats. Both also have a visible on-screen control.
    this.input.keyboard?.on('keydown', (ev: KeyboardEvent) => {
      if (document.activeElement === this.inputEl) return;
      if (ev.key === ',') {
        audio().unlock();
        this.hideTopicInput();
        openSettings(this);
      } else if (ev.key === 't' || ev.key === 'T') {
        audio().unlock();
        this.hideTopicInput();
        openStats(this);
      } else if (ev.key === 'h' || ev.key === 'H' || ev.key === '?') {
        audio().unlock();
        this.openHowToPlay();
      } else if (ev.key === 'i' || ev.key === 'I') {
        audio().unlock();
        this.hideTopicInput();
        openDisclosure(this);
      } else if (ev.key === 'l' || ev.key === 'L') {
        audio().unlock();
        this.openCustomLabPanel();
      }
    });
  }

  // --- PROGRESSION: rank chip + daily-missions strip -------------------------
  /** A compact RANK chip (name + thin XP progress bar) top-left; tap opens the Stats panel. */
  private buildRankChip(W: number, H: number): void {
    const prog = getProgress();
    const cx = W / 2;
    const s = Math.max(34, Math.min(42, W * 0.06));
    const y = Math.max(30, s * 0.72); // same row as the top-right icons + missions pill
    const h = s;
    const left = 12;
    const padX = 10;
    const color = prog.maxed ? PALETTE.correct : PALETTE.player;
    const crownS = h * 0.4;
    const fontPx = scaledFont(Math.max(11, Math.min(13, h * 0.34)), 11);
    const nameTxt = this.add
      .text(0, y - h * 0.16, `LV ${prog.rank + 1} ${prog.rankName}`.toUpperCase(), { fontFamily: FONT.display, fontSize: `${fontPx}px`, color: hex(color), fontStyle: 'bold' })
      .setOrigin(0, 0.5)
      .setDepth(9_001);
    let w = padX + crownS + 6 + nameTxt.width + padX;
    // Keep the pill clear of the centered missions pill so they never collide at 360px.
    const maxW = Math.max(108, cx - 56 - left);
    if (w > maxW) {
      const textRoom = maxW - (padX + crownS + 6 + padX);
      if (nameTxt.width > textRoom && textRoom > 0) nameTxt.setScale(textRoom / nameTxt.width);
      w = maxW;
    }
    const g = this.add.graphics().setDepth(9_000);
    g.fillStyle(PALETTE.surface, 0.9);
    g.fillRoundedRect(left, y - h / 2, w, h, h / 2);
    g.lineStyle(1.25, color, 0.8);
    g.strokeRoundedRect(left, y - h / 2, w, h, h / 2);
    icon(this, 'crown', left + padX + crownS / 2, y - h * 0.16, crownS, color).setDepth(9_001);
    nameTxt.setX(left + padX + crownS + 6);
    // Thin XP progress bar under the name (the at-a-glance progress-to-next).
    const bar = progressBar(this, left + padX, y + h * 0.26, w - padX * 2, Math.max(3, h * 0.12), prog.pctToNext, color);
    bar.container.setDepth(9_001);
    const hit = this.add.rectangle(left + w / 2, y, w, h, 0xffffff, 0.001).setInteractive({ useHandCursor: true }).setDepth(9_002);
    hit.on('pointerup', () => {
      audio().unlock();
      audio().playSfx('uiTap');
      this.hideTopicInput();
      openStats(this);
    });
    void H;
  }

  /**
   * A restrained DAILY MISSIONS badge in the top row (next to the rank chip), NOT a stacked block — a
   * toy-first menu must never crowd the hero diorama. It shows "🎯 MISSIONS x/3" (with a tiny ✓ when
   * all three are done) and taps through to the Stats panel, where today's 3 missions are listed in
   * full. A subtle amber pulse draws the eye while missions remain open.
   */
  private buildMissionsBadge(W: number, H: number): void {
    const missions: MissionView[] = getDailyMissions();
    if (missions.length === 0) return;
    const doneCount = missions.filter((m) => m.done).length;
    const allDone = doneCount >= missions.length;
    const size = Math.max(34, Math.min(42, W * 0.06));
    const y = Math.max(30, size * 0.72); // same top row as the rank chip + the top-right icons
    const color = allDone ? PALETTE.correct : PALETTE.armed;

    // A geometric target/check icon double-codes the state without relying on hue (colorblind-safe):
    // `check` when all 3 are done, else `target` with the x/3 count. Centered on the top row.
    const c = chip(this, W / 2, y, `GOALS ${doneCount}/${missions.length}`, {
      icon: allDone ? 'check' : 'target',
      textColor: color,
      borderColor: color,
      fill: PALETTE.surface,
      fontPx: scaledFont(Math.max(11, Math.min(13, W * 0.024)), 11),
      originX: 0.5,
      onClick: () => {
        this.hideTopicInput();
        openStats(this);
      },
    }).setDepth(9_000);
    if (!allDone && motionAllowed()) {
      this.tweens.add({ targets: c, alpha: { from: 1, to: 0.62 }, duration: 900, yoyo: true, repeat: -1, ease: 'Sine.easeInOut' });
    }
    void H;
  }

  /**
   * P2.5 one-time "SKIN UNLOCKED!" celebration, consumed from takeUnlockCelebrations() on
   * returning-hub enter (the single celebration site — each skin toasts exactly once, ever). A gold
   * banner below the top chip row; tap opens the locker; auto-dismisses. Motion-gated slide/fade;
   * reduced-motion shows it statically for the same dwell.
   */
  private celebrateSkinUnlocks(): void {
    const fresh = takeUnlockCelebrations();
    if (fresh.length === 0) return;
    const W = this.scale.width;
    const w = Math.min(W * 0.86, 470);
    const h = 58;
    const y = 96;
    const first = fresh[0];
    const c = this.add.container(W / 2, y).setDepth(9_500);
    const g = this.add.graphics();
    g.fillStyle(PALETTE.surface2, 0.98);
    g.fillRoundedRect(-w / 2, -h / 2, w, h, 12);
    g.lineStyle(1.5, PALETTE.gold, 0.95);
    g.strokeRoundedRect(-w / 2, -h / 2, w, h, 12);
    c.add(g);
    c.add(icon(this, 'palette', -w / 2 + 26, 0, 20, first.tint ?? PALETTE.player));
    const title = fresh.length > 1 ? `${fresh.length} SKINS UNLOCKED!` : 'SKIN UNLOCKED!';
    const sub = fresh.length > 1 ? fresh.map((f) => f.label).join(' · ') : `${first.label} — tap to equip`;
    c.add(
      this.add
        .text(-w / 2 + 46, -9, title, { fontFamily: FONT.display, fontSize: `${scaledFont(15, 12)}px`, color: hex(PALETTE.gold), fontStyle: 'bold' })
        .setOrigin(0, 0.5),
    );
    c.add(
      this.add
        .text(-w / 2 + 46, 11, sub, { fontFamily: FONT.family, fontSize: `${scaledFont(12, 11)}px`, color: hex(PALETTE.text) })
        .setOrigin(0, 0.5),
    );
    const hit = this.add.rectangle(0, 0, w, h, 0xffffff, 0.001).setInteractive({ useHandCursor: true });
    c.add(hit);
    let gone = false;
    const dismiss = (): void => {
      if (gone || !c.active) return;
      gone = true;
      if (motionAllowed()) {
        this.tweens.add({ targets: c, alpha: 0, y: y - 14, duration: 220, ease: 'Sine.easeIn', onComplete: () => c.destroy() });
      } else {
        c.destroy();
      }
    };
    hit.on('pointerup', () => {
      audio().unlock();
      audio().playSfx('uiConfirm');
      dismiss();
      this.hideTopicInput();
      openLocker(this);
    });
    if (motionAllowed()) {
      c.setAlpha(0).setY(y - 18);
      this.tweens.add({ targets: c, alpha: 1, y, duration: 260, ease: 'Back.easeOut' });
    }
    audio().playSfx('perfect', { volume: 0.6 });
    this.time.delayedCall(5000, dismiss);
  }

  // --- topic input overlay (extracted; see menu/topicInput.ts) ---
  createTopicInput = TopicInput.createTopicInput;
  positionInput = TopicInput.positionInput;
  hideTopicInput = TopicInput.hideTopicInput;
  teardownOverlay = TopicInput.teardownOverlay;

  // --- actions ---------------------------------------------------------------
  // Public: called by the extracted topicInput controller (Enter key in the DOM overlay).
  submitTyped(): void {
    const raw = (this.inputEl?.value ?? '').trim();
    if (raw.length < 2) {
      this.showMessage('Type a factual topic first — e.g. “Dinosaurs”, “US Presidents”.', PALETTE.textDim);
      this.inputEl?.focus();
      return;
    }
    track('custom_topic_requested', { result: 'typed', topicCategory: raw });
    this.playTopic(raw, 'custom');
  }

  /** P2.4: open the Custom Lab modal, pre-filled from the inline topic box. Its BUILD & PLAY routes
   *  back through launchCustomTopic so the verify pipeline + analytics are reused (no new fetch path). */
  private openCustomLabPanel(): void {
    if (this.loading || this.onboarding) return;
    audio().unlock();
    this.hideTopicInput();
    openCustomLab(this, {
      initialTopic: this.inputEl?.value ?? '',
      onLaunch: (raw: string) => this.launchCustomTopic(raw),
    });
  }

  private openOfflineFallbackPanel(): void {
    if (this.offlinePanelShown || this.loading || this.onboarding) return;
    this.offlinePanelShown = true;
    this.hideTopicInput();
    openOfflinePanel(this, {
      onPlayOffline: () => this.playSelectedSeed(),
      onRetry: () => {
        if (typeof window !== 'undefined') {
          try {
            window.location.reload();
          } catch {
            // Headless / blocked reload: the panel has already closed.
          }
        }
      },
    });
  }

  /** P2.4: the Custom Lab launch path — mirrors submitTyped's analytics, then plays via playTopic. */
  private launchCustomTopic(raw: string): void {
    const topic = raw.trim();
    if (topic.length < 2) return;
    track('custom_topic_requested', { result: 'custom_lab', topicCategory: topic });
    this.playTopic(topic, 'custom');
  }

  private surpriseMe(): void {
    // Random seed topic — populate the input so the player sees what they're getting, then play it.
    const pick = SEED_TOPICS[Math.floor(Math.random() * SEED_TOPICS.length)];
    if (this.inputEl) this.inputEl.value = pick.raw;
    this.playTopic(pick.raw, 'surprise');
  }

  private playSelectedSeed(): void {
    const boot = readBoot(this);
    if (!boot.debug && !onboardingSeen()) {
      this.startNoviceRun();
      return;
    }
    const row = this.rows.find((r) => r.normalized === this.selected) ?? this.rows[0];
    if (row) this.playTopic(row.raw, 'selected_seed');
  }

  private startNoviceRun(): void {
    if (this.loading) return;
    // Lock immediately: this path does NOT call beginLoading(), so without this a double-tap of
    // START TRAINING would fire two scene.start()s + two 'tutorial_started' events (the old guard
    // was dead because nothing ever set this.loading on the novice path).
    this.loading = true;
    const boot = readBoot(this);
    const content = noviceBoardFor(0);
    track('tutorial_started', {
      boardSource: 'seed',
      topicCategory: NOVICE_TOPIC_CATEGORY,
      difficulty: 1,
      boardIndex: 0,
    });
    this.registry.set(REG.topic, content.topicNormalized);
    this.registry.set(REG.ai, noviceAiEnabled(0));
    this.registry.set(REG.daily, false);
    this.registry.set(REG.boardIndex, 0);
    this.teardownOverlay();
    this.scene.start('Game', {
      topic: content.topicNormalized,
      topicLabel: content.topicRaw,
      content,
      seed: boot.seed,
      ai: noviceAiEnabled(0),
      rivalWarmup: false,
      boardIndex: 0,
      isDaily: false,
      boardSource: 'seed',
      noviceRun: true,
    });
  }

  /** The single funnel for "play a generated/fallback board for this topic". */
  private playTopic(topicRaw: string, kind: LaunchKind = 'quick_pick'): void {
    if (this.loading) return;
    // First-run gating is CENTRALIZED here so EVERY discovery entry point (surprise, custom topic,
    // recents, quick-pick) routes a brand-new player into the training run — not just the PLAY hero.
    // The old auto-forced tutorial overlay was deleted in P0.3, so this is the only thing between a
    // first-timer and a full hazard/timer/rival board. Deep links keep their own first-run handling
    // (openHowToPlay) so a shared topic still plays the shared topic.
    if (kind !== 'deep_link' && !readBoot(this).debug && !onboardingSeen()) {
      this.startNoviceRun();
      return;
    }
    this.clearMessage();
    this.launchingDaily = false;
    this.launchingRaceGhost = false;
    this.launchingLadder = null; // a topic/quick-pick launch is never a ladder duel
    this.launchingLabel = topicRaw;
    this.launchingKind = kind;
    this.launchingBoardSource = 'unknown';
    if (kind !== 'custom') track('topic_selected', { result: kind, topicCategory: topicRaw });
    // inflight is set BEFORE beginLoading so the overlay knows a real request drives the checklist
    // (the devBridge 'board-cook' capture hook calls beginLoading with no request in flight).
    const ctrl = new AbortController();
    this.inflight = ctrl;
    this.beginLoading('building verified board…');
    fetchBoard(topicRaw, { signal: ctrl.signal, onPhase: (p) => this.onCookPhase(p) })
      .then((res) => this.onBoardReady(topicRaw, res))
      .catch((err: unknown) => this.onBoardFailed(err));
  }

  private playDaily(opts?: { raceGhost?: boolean; ladder?: LadderRace | null }): void {
    if (this.loading) return;
    // A brand-new player who taps DAILY before finishing training is eased in via the training run
    // too (the Daily board carries the full timer/hazards). Deep-linked dailies have already passed
    // through openHowToPlay (which marks onboarding seen) by the time this runs, so they play Daily.
    if (!readBoot(this).debug && !onboardingSeen()) {
      this.startNoviceRun();
      return;
    }
    this.clearMessage();
    this.launchingDaily = true;
    // GHOST DUELS Slice A: carry the self-race intent into startWithContent. GameScene fails open to a
    // plain daily if the saved ghost's bv/seed/mutator/topic no longer pin — the CTA is never blocked.
    this.launchingRaceGhost = opts?.raceGhost === true;
    // GHOST DUELS Slice C: carry the chosen ladder challenger (record + rung). GameScene pin-checks it and
    // fails open to a plain daily on a miss; startWithContent forces the rival ON for the 3-way duel.
    this.launchingLadder = opts?.ladder ?? null;
    this.launchingKind = 'daily';
    this.launchingBoardSource = 'unknown';
    track('topic_selected', { result: 'daily' });
    const ctrl = new AbortController();
    this.inflight = ctrl;
    this.beginLoading('checking today’s board…');
    getDaily({ signal: ctrl.signal, onPhase: (p) => this.onCookPhase(p) })
      .then((res) => {
        this.launchingLabel = res.board.topicRaw;
        this.onBoardReady(res.board.topicRaw, res);
      })
      .catch((err: unknown) => this.onBoardFailed(err));
  }

  private onBoardReady(topicRaw: string, res: FetchBoardResult): void {
    if (!this.scene.isActive()) return; // scene left while in flight
    this.inflight = undefined;
    this.rememberRecent(topicRaw);
    this.launchingBoardSource = boardSourceFor(res);
    if (this.launchingKind === 'custom') {
      track('custom_topic_succeeded', {
        boardSource: this.launchingBoardSource,
        topicCategory: topicRaw,
        result: res.origin,
        failureReason: res.fallbackReason,
      });
    }
    if (res.origin === 'seed' && res.fallbackReason) {
      // Couldn't generate live, but a matching classic board exists — play it and say WHY briefly,
      // so the player understands (e.g. "going fast", "offline") rather than seeing an unexplained
      // topic swap. The reason-specific copy mirrors boardClient's friendly map (G12).
      const reason = res.fallbackReason;
      // The "here's a hint" reasons linger a touch longer so the player reads them (incl. the
      // cache-first "your verified board is being built, ready next time" note).
      const lingerMs =
        reason === 'rate_limited' || reason === 'unprocessable' || reason === 'generating' ? 1200 : 800;
      this.finishCooking('seed', () => this.flashThenStart(res.board, this.seedNoteFor(reason), lingerMs));
      return;
    }
    this.finishCooking('generated', () => this.startWithContent(res.board));
  }

  /**
   * Short inline note shown when we fell back to a matching seed board (G12). One line per failure
   * mode so the swap is never a silent dead-end: the player learns it's offline / rate-limited /
   * the topic was rejected, and that we kept them playing on a close classic board anyway.
   */
  private seedNoteFor(reason: BoardErrorReason): string {
    switch (reason) {
      case 'rate_limited':
        return "You're going fast — playing a matching cached board for now.";
      case 'unprocessable':
        return 'That topic was hard to verify — here’s a matching factual board instead.';
      case 'offline':
        return "Looks like you're offline — playing a matching classic board.";
      case 'timeout':
        return 'The verifier was slow — playing a matching classic board.';
      case 'generating':
        return '✨ Building your verified board — playing a quick one now, it’ll be ready next time!';
      case 'unavailable':
      case 'bad_response':
      default:
        return 'Live board service busy — playing a matching classic board.';
    }
  }

  private onBoardFailed(err: unknown): void {
    if (!this.scene.isActive()) return;
    if (err instanceof DOMException && err.name === 'AbortError') return; // we cancelled
    this.inflight = undefined;
    this.endLoading();
    const reason = (err as { reason?: BoardErrorReason })?.reason;
    const friendly =
      typeof (err as { friendly?: unknown })?.friendly === 'string'
        ? (err as { friendly: string }).friendly
        : 'Could not make a board. Try a quick-pick topic to keep playing.';
    // 'generating' is a WAIT, not an error: the verified board is still cooking in the background. Show
    // it calmly (amber) and keep the typed topic in the field so BUILD again is one tap — by then the
    // background gen has cached the real board and the poll returns it. Never a silent wrong-topic swap.
    this.showMessage(friendly, reason === 'generating' ? PALETTE.armed : PALETTE.hazard);
  }

  /** Brief inline note, then start the board (used for the seed-fallback path). */
  private flashThenStart(content: BoardContent, note: string, delayMs = 700): void {
    this.showMessage(note, PALETTE.armed);
    this.time.delayedCall(delayMs, () => {
      if (this.scene.isActive()) this.startWithContent(content);
    });
  }

  private startWithContent(content: BoardContent): void {
    const boot = readBoot(this);
    // GHOST DUELS Slice C: a ladder duel is you vs BYT3 vs the challenger's ghost — force the rival ON and
    // skip the warmup so BYT3 is present on the daily board itself (the gate already required opting in).
    const ladder = this.launchingDaily ? this.launchingLadder : null;
    const rivalWarmup = ladder ? false : this.shouldRivalWarmup();
    // P1.2 challenge: only a deep-linked board (?target=N) carries a friend's score to beat. Gating on
    // the deep_link launch kind means a stale registry target can never leak into a later manual play.
    // Challenge payload (target/ghost/series) is carried only when the launch came from a deep link
    // (the 'deep_link' topic kind OR the daily branch of a deep-linked challenge), so a stale registry
    // value can never leak into a later manual play.
    const fromDeepLink = this.launchingKind === 'deep_link' || this.launchedFromDeepLink;
    const challengeTarget = fromDeepLink ? boot.challengeTarget : 0;
    const challengeGhost = fromDeepLink ? boot.challengeGhost : '';
    const series: SeriesState | null = fromDeepLink ? parseSeries(boot) : null;
    // A ladder duel forces the rival on (ai=true, no warmup); every other launch keeps the toggle/warmup.
    const aiForRun = ladder ? true : rivalWarmup ? false : this.aiEnabled;
    this.registry.set(REG.topic, content.topicNormalized);
    this.registry.set(REG.ai, ladder ? true : this.aiEnabled);
    this.registry.set(REG.daily, this.launchingDaily);
    this.registry.set(REG.boardIndex, 0);
    const topicLabel = resolveLaunchTopicLabel(this.launchingLabel, content);
    this.teardownOverlay();
    this.scene.start('Game', {
      topic: content.topicNormalized,
      topicLabel,
      content,
      seed: boot.seed,
      ai: aiForRun,
      rivalWarmup,
      boardIndex: 0,
      isDaily: this.launchingDaily,
      boardSource: this.launchingBoardSource,
      challengeTarget,
      challengeGhost,
      ghostSource: this.launchingDaily && this.launchingRaceGhost ? 'last-daily' : '',
      // GHOST DUELS Slice C: the ladder challenger to race (null on every non-ladder launch).
      ladderGhost: ladder,
      series,
    });
  }

  private shouldRivalWarmup(): boolean {
    if (!this.aiEnabled) return false;
    // Explicit URLs and harness paths that request ai=1 should remain direct race paths.
    return !new URLSearchParams(window.location.search).has('ai');
  }

  // --- loading / cook overlay (extracted; see menu/loadingOverlay.ts) ---
  beginLoading = LoadingOverlay.beginLoading;
  setCookStep = LoadingOverlay.setCookStep;
  advanceCook = LoadingOverlay.advanceCook;
  onCookPhase = LoadingOverlay.onCookPhase;
  markCookAccepted = LoadingOverlay.markCookAccepted;
  finishCooking = LoadingOverlay.finishCooking;
  endLoading = LoadingOverlay.endLoading;

  // --- inline messages -------------------------------------------------------
  private showMessage(msg: string, color: number): void {
    this.messageText?.setText(msg).setColor(hex(color));
  }
  private clearMessage(): void {
    this.messageText?.setText('');
  }

  // --- recents (localStorage) ------------------------------------------------
  private readRecents(): string[] {
    try {
      const raw = window.localStorage.getItem(RECENTS_KEY);
      if (!raw) return [];
      const arr: unknown = JSON.parse(raw);
      if (!Array.isArray(arr)) return [];
      return arr.filter((x): x is string => typeof x === 'string').slice(0, MAX_RECENTS);
    } catch {
      return [];
    }
  }
  private rememberRecent(topicRaw: string): void {
    const t = topicRaw.trim();
    if (!t) return;
    try {
      const prev = this.readRecents().filter((r) => r.toLowerCase() !== t.toLowerCase());
      const next = [t, ...prev].slice(0, MAX_RECENTS);
      window.localStorage.setItem(RECENTS_KEY, JSON.stringify(next));
    } catch {
      // localStorage unavailable (private mode / blocked) — recents are best-effort.
    }
  }

  // --- rival mode preference (localStorage) ----------------------------------
  /** The returning user's stored mode choice: true=RIVAL, false=PRACTICE, null=never chosen. */
  private readModePref(): boolean | null {
    try {
      const raw = window.localStorage.getItem(MODE_PREF_KEY);
      if (raw === 'rival') return true;
      if (raw === 'practice') return false;
      return null;
    } catch {
      return null;
    }
  }
  private writeModePref(rival: boolean): void {
    try {
      window.localStorage.setItem(MODE_PREF_KEY, rival ? 'rival' : 'practice');
    } catch {
      // localStorage unavailable (private mode / blocked) — the mode preference is best-effort.
    }
  }

}

function boardSourceFor(res: FetchBoardResult): BoardSource {
  if (res.origin === 'generated') return 'generated';
  return res.fallbackReason ? 'fallback' : 'seed';
}
